import React, { useState, useEffect } from 'react';
import BlogCard from '../components/BlogCard';
import PixieDust from '../components/PixieDust';
import StarField from '../components/StarField';
import { Reveal } from '../utils/useReveal';
import './Blog.css';

const S3_BASE = 'https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);

  const categories = ['All', 'Resorts', 'Dining', 'Parks', 'Tips', 'News', 'Sales'];

  useEffect(() => {
    document.title = 'Disney Travel Blog — Tips, Resorts & Magic | Magically Mousebound';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Expert Disney travel tips, resort reviews, dining guides, and magical insights from Chrystal Harris — your personal Disney Travel Expert.');
    }
    return () => { document.title = 'Magically Mousebound'; };
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);

        // Fetch manifest — a simple list of slugs, no credentials needed
        const manifestRes = await fetch(`${S3_BASE}/blog-posts/manifest.json`);
        if (!manifestRes.ok) throw new Error('Manifest not found');
        const { posts: slugs } = await manifestRes.json();

        // Fetch each post metadata in parallel
        const settled = await Promise.allSettled(
          slugs.map(slug =>
            fetch(`${S3_BASE}/blog-posts/metadata/${slug}.json`).then(r => r.json())
          )
        );

        const posts = settled
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value);

        posts.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(posts);
        setFilteredPosts(posts);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setPosts([]);
        setFilteredPosts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setFilteredPosts(category === 'All' ? posts : posts.filter(p => p.category === category));
  };

  const featuredPost = filteredPosts.find(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured || selectedCategory !== 'All');
  const gridPosts = selectedCategory === 'All' && featuredPost
    ? filteredPosts.filter(p => !p.featured)
    : filteredPosts;

  if (isLoading) {
    return (
      <div className="blog-loading" aria-live="polite" aria-label="Loading blog posts">
        <div className="blog-loading-inner">
          <div className="blog-spinner" aria-hidden="true"></div>
          <p>Loading magical content…</p>
        </div>
      </div>
    );
  }

  return (
    <main className="blog-page">
      {/* Hero */}
      <section className="blog-hero" aria-label="Blog header">
        <StarField count={30} />
        <PixieDust count={10} />
        <div className="container">
          <Reveal className="blog-hero-badge">✨ Disney Travel Insider</Reveal>
          <Reveal as="h1" delay={1}>Tips, Magic &amp; Disney Secrets</Reveal>
          <Reveal as="p" delay={2}>Expert guides, resort reviews, and real family experiences from your personal Disney Travel Expert, <strong>Chrystal Harris</strong>.</Reveal>
          {posts.length > 0 && (
            <Reveal className="blog-hero-count" delay={3} aria-label={`${posts.length} articles available`}>
              {posts.length} article{posts.length !== 1 ? 's' : ''} and counting
            </Reveal>
          )}
        </div>
      </section>

      <section className="blog-content" aria-label="Blog posts">
        <div className="container">

          {/* Category Filters */}
          <nav className="category-filters" aria-label="Filter posts by category">
            {categories.map(category => (
              <button
                key={category}
                className={`category-filter ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryFilter(category)}
                aria-pressed={selectedCategory === category}
              >
                {category}
              </button>
            ))}
          </nav>

          {filteredPosts.length > 0 ? (
            <>
              {/* Featured hero card */}
              {selectedCategory === 'All' && featuredPost && (
                <Reveal className="featured-post-wrap shimmer-sweep">
                  <div className="featured-label" aria-label="Featured article">Featured</div>
                  <BlogCard post={featuredPost} variant="featured" />
                </Reveal>
              )}

              {/* Regular grid */}
              {gridPosts.length > 0 && (
                <div className="posts-grid" role="list">
                  {gridPosts.map((post, i) => (
                    <Reveal key={post.slug || post.id} delay={(i % 3) + 1} role="listitem">
                      <BlogCard post={post} />
                    </Reveal>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="no-posts" role="status">
              <div className="no-posts-icon" aria-hidden="true">🏰</div>
              <h2>More Magic Coming Soon</h2>
              <p>Chrystal is crafting amazing Disney travel content for you — tips, resort reviews, dining guides, and secrets only insiders know.</p>
            </div>
          )}

          {filteredPosts.length > 0 && (
            <p className="results-summary" aria-live="polite">
              {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </p>
          )}
        </div>
      </section>
    </main>
  );
};

export default Blog;
